import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { UserService, AdminUserDetails } from "@/services/UserService";
import { API_URL } from "@/constants/api";
import { DeeplinkButton } from "@/components/ui/DeeplinkButton";

interface UserProfileDialogProps {
  userId: number;
  username?: string;
  isOpen: boolean;
  accessToken: string;
  onClose: () => void;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({
  userId,
  username,
  isOpen,
  accessToken,
  onClose,
}) => {
  const [user, setUser] = useState<AdminUserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserDetails();
    }
  }, [isOpen, userId]);

  const fetchUserDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userData = await UserService.getUserById(userId, accessToken);
      if (userData) {
        setUser(userData);
      } else {
        setError("User not found");
      }
    } catch (err) {
      setError("Failed to load user details");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getProfileImageUrl = (profilePicId: string | undefined): string => {
    if (!profilePicId) return "";
    return `${API_URL}/image/profile/${profilePicId}`;
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <svg className="animate-spin h-10 w-10 text-blue-600 mb-4" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <p className="text-gray-500">Loading user details...</p>
    </div>
  );

  const renderError = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <p className="text-gray-700 font-medium">{error}</p>
      <button
        onClick={fetchUserDetails}
        className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
      >
        Retry
      </button>
    </div>
  );

  const renderContent = () => {
    if (!user) return null;

    const profileImageUrl = getProfileImageUrl(user.profilePicId);

    return (
      <>
        {/* Header with profile image */}
        <div className="flex items-center gap-4 mb-6">
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt={user.name || user.username}
              className="w-20 h-20 rounded-full object-cover border-4 border-gray-100"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-white text-2xl font-bold">
                {(user.name || user.username || "?").charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h3 className="text-xl font-bold text-gray-900">{user.name || "No name"}</h3>
            <p className="text-sm text-gray-500">{user.username}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-400">ID:</span>
              <DeeplinkButton
                userId={user.userId}
                variant="badge"
                title="Open in Users page"
              />
            </div>
          </div>
        </div>

        {/* Pro status badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {user.ispro && (
            <span className="px-2.5 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-semibold rounded-full">
              PRO
            </span>
          )}
          {user.freesubscription && (
            <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
              Free Trial
            </span>
          )}
          {user.privateAccount && (
            <span className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
              Private
            </span>
          )}
          {!user.lbVisible && (
            <span className="px-2.5 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
              Hidden from LB
            </span>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">{user.sessionsCount ?? 0}</div>
            <div className="text-xs text-gray-500">Sessions</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">{user.followersCount ?? 0}</div>
            <div className="text-xs text-gray-500">Followers</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">{user.followingCount ?? 0}</div>
            <div className="text-xs text-gray-500">Following</div>
          </div>
        </div>

        {/* Personal bests */}
        {(user.maxheight || user.maxairtime || user.maxspeed) && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Personal Bests</h4>
            <div className="grid grid-cols-3 gap-3">
              {user.maxheight !== undefined && (
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-blue-700">{user.maxheight.toFixed(1)}m</div>
                  <div className="text-xs text-blue-600">Max Height</div>
                </div>
              )}
              {user.maxairtime !== undefined && (
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-green-700">{user.maxairtime.toFixed(1)}s</div>
                  <div className="text-xs text-green-600">Max Airtime</div>
                </div>
              )}
              {user.maxspeed !== undefined && (
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-purple-700">{user.maxspeed.toFixed(0)} km/h</div>
                  <div className="text-xs text-purple-600">Max Speed</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Details */}
        <div className="space-y-2 text-sm">
          {user.gender && (
            <div className="flex justify-between">
              <span className="text-gray-500">Gender</span>
              <span className="text-gray-900 capitalize">{user.gender.toLowerCase()}</span>
            </div>
          )}
          {user.preferredSport && (
            <div className="flex justify-between">
              <span className="text-gray-500">Sport</span>
              <span className="text-gray-900 capitalize">{user.preferredSport.toLowerCase()}</span>
            </div>
          )}
          {user.skillLevel && (
            <div className="flex justify-between">
              <span className="text-gray-500">Skill Level</span>
              <span className="text-gray-900 capitalize">{user.skillLevel.toLowerCase()}</span>
            </div>
          )}
          {user.homeSpotName && (
            <div className="flex justify-between">
              <span className="text-gray-500">Home Spot</span>
              <span className="text-gray-900">{user.homeSpotName}</span>
            </div>
          )}
          {user.createdAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">Member Since</span>
              <span className="text-gray-900">{formatDate(user.createdAt)}</span>
            </div>
          )}
          {user.kiteCount !== undefined && (
            <div className="flex justify-between">
              <span className="text-gray-500">Kites</span>
              <span className="text-gray-900">{user.kiteCount} active, {user.archivedKiteCount ?? 0} archived</span>
            </div>
          )}
        </div>

        {/* Connections */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Connections</h4>
          <div className="flex flex-wrap gap-2">
            {user.hasGarminConnected && (
              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Garmin
              </span>
            )}
            {user.hasStravaConnected && (
              <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Strava
              </span>
            )}
            {user.hasWatch && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Watch
              </span>
            )}
          </div>
        </div>

        {/* Social media */}
        {(user.socialInstagram || user.socialTiktok) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Social</h4>
            <div className="flex flex-wrap gap-2">
              {user.socialInstagram && (
                <a
                  href={`https://instagram.com/${user.socialInstagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded flex items-center gap-1 hover:bg-pink-200 transition-colors"
                >
                  @{user.socialInstagram}
                </a>
              )}
              {user.socialTiktok && (
                <a
                  href={`https://tiktok.com/@${user.socialTiktok}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1 hover:bg-gray-200 transition-colors"
                >
                  @{user.socialTiktok}
                </a>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  const dialog = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">User Profile</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4">
          {isLoading && renderLoading()}
          {!isLoading && error && renderError()}
          {!isLoading && !error && renderContent()}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
};

export default UserProfileDialog;
